import React, { FC, useCallback, useState } from "react"
import { useIntl } from "react-intl"
import { observer } from "mobx-react-lite"
import cn from "classnames"

import messages from "messages/common"
import accountMessages from "messages/account"
import financeMessages from "messages/finance"
import { useMst } from "models/Root"
import { routes } from "constants/routing"
import useAccount from "hooks/useAccount"
import InternalLink from "components/InternalLink"
import Account from "layouts/RedesignedLayout/navigation/contents/Account"
import Wallet from "layouts/RedesignedLayout/navigation/contents/Wallet"

import styles from "components/NewUI/Header/Navigation/index.module.scss"
import IconPassport from "assets/icons/menu/passport.svg"
import IconUserPlus from "assets/icons/menu/user-plus.svg"
import IconUsersArrow from "assets/icons/menu/users-arrow.svg"
import IconSecurity from "assets/icons/menu/security.svg"
import IconSpot from "assets/icons/menu/spot.svg"
import IconLogout from "assets/icons/menu/logout.svg"
import IconSettings from "assets/icons/menu/settings.svg"
import IconNotifications from "assets/icons/menu/notification.svg"
import NotificationsModal from "components/v3/NotificationsModal"
import IconTransfer from "assets/icons/menu/transfer.svg"
import IconDatabase from "assets/icons/menu/database.svg"
import IconClockRewind from "assets/icons/menu/clock-rewind.svg"

const User: FC<{ isMobile?: boolean; onClose?: () => void }> = ({ isMobile = false, onClose }) => {
	const { formatMessage } = useIntl()
	const {
		render,
		account: { profileStatus },
	} = useMst()
	const { verify, logout } = useAccount()
	const [isNotificationsModalOpened, toggleNotificationsModal] = useState(false)

	// const { data: tradingFees } = useTradingFees()
	// const copyAction = useCopyClick()

	const handleLogout = async (e: React.MouseEvent) => {
		e.preventDefault()
		await logout()
	}

	// const handleCopyId = useCallback(() => {
	// 	if (profileStatus?.uid) {
	// 		copyAction(profileStatus?.uid)
	// 	}
	// }, [profileStatus?.uid])

	return (
		<>
			<div
				className={`${!isMobile ? styles.nav__firstList : ""} ${
					!isMobile ? styles.nav__firstList___user : ""
				}`}
			>
				<div className={styles.nav__userData}>
					<Account />
					<Wallet />
				</div>

				<ul className={styles.nav__userList}>
					{/* @TODO: Refactor menu items after all of them will be confirmed */}
					<li className={styles.nav__group}>{formatMessage(accountMessages.manage_account)}</li>
					<li className={styles.nav__itemFirst}>
						<div className={styles.nav__userButton} onClick={() => toggleNotificationsModal(true)}>
							<span>
								<img
									src={IconNotifications}
									width={20}
									height={20}
									alt={formatMessage(messages.notifications)}
								/>
								<span>{formatMessage(messages.notifications)}</span>
							</span>
						</div>
					</li>

					{!(!render.referrals || profileStatus?.is_sub_account) ? (
						<li className={styles.nav__itemFirst}>
							<InternalLink to={routes.referrals.root} className={styles.nav__userButton}>
								<span>
									<img
										src={IconUserPlus}
										width={20}
										height={20}
										alt={formatMessage(messages.referrals)}
									/>
									<span>{formatMessage(messages.referrals)}</span>
								</span>
							</InternalLink>
						</li>
					) : null}

					{!(!profileStatus?.is_email_confirmed || profileStatus?.is_sub_account) ? (
						<li className={styles.nav__itemFirst}>
							<InternalLink to={routes.verification.root} className={styles.nav__userButton}>
								<span>
									<img
										src={IconPassport}
										width={20}
										height={20}
										alt={formatMessage(messages.verification)}
									/>
									<span>{formatMessage(messages.verification)}</span>
								</span>
							</InternalLink>
						</li>
					) : null}

					<li className={styles.nav__itemFirst}>
						<InternalLink to={routes.tradingFees} className={styles.nav__userButton}>
							<span>
								<img src={IconSpot} width={20} height={20} alt={formatMessage(messages.fee)} />
								{/*<span>{formatMessage(messages.fee)}</span>*/}
								<span>My Fee Rates</span>
							</span>
						</InternalLink>
					</li>

					<li className={styles.nav__itemFirst}>
						<InternalLink to={routes.security.root} className={styles.nav__userButton}>
							<span>
								<img
									src={IconSecurity}
									width={20}
									height={20}
									alt={formatMessage(messages.security)}
								/>
								<span>{formatMessage(messages.security)}</span>
							</span>
						</InternalLink>
					</li>

					{render.subAccounts && !profileStatus?.is_sub_account ? (
						<li className={styles.nav__itemFirst}>
							<InternalLink to={routes.subAccounts.balances} className={styles.nav__userButton}>
								<span>
									<img
										src={IconUsersArrow}
										width={20}
										height={20}
										alt={formatMessage(messages.sub_accounts)}
									/>
									<span>{formatMessage(messages.sub_accounts)}</span>
								</span>
							</InternalLink>
						</li>
					) : null}

					<li className={styles.nav__group}>{formatMessage(accountMessages.accounts)}</li>

					<li className={styles.nav__itemFirst}>
						<InternalLink to={routes.profile.wallets} className={styles.nav__userButton}>
							<span>
								<img
									src={IconDatabase}
									width={20}
									height={20}
									alt={formatMessage(financeMessages.wallet_spot)}
								/>
								<span>{formatMessage(financeMessages.wallet_spot)}</span>
							</span>
						</InternalLink>
					</li>

					<li className={cn(styles.nav__itemFirst, styles.nav__itemFirst___disabled)}>
						<InternalLink to={routes.profile.wallets} className={styles.nav__userButton}>
							<span>
								<img src={IconDatabase} width={20} height={20} alt={formatMessage(messages.api)} />
								<span>Funding account</span>
							</span>
						</InternalLink>
					</li>

					<li className={cn(styles.nav__itemFirst, styles.nav__itemFirst___disabled)}>
						<InternalLink to={routes.profile.wallets} className={styles.nav__userButton}>
							<span>
								<img src={IconDatabase} width={20} height={20} alt={formatMessage(messages.api)} />
								<span>Unified Trading</span>
							</span>
						</InternalLink>
					</li>

					<li className={styles.nav__group}>{formatMessage(financeMessages.manage_crypto)}</li>

					<li className={styles.nav__itemFirst}>
						<InternalLink
							to={verify(routes.profile.createTransfer)}
							className={styles.nav__userButton}
						>
							<span>
								<img
									src={IconTransfer}
									width={20}
									height={20}
									alt={formatMessage(messages.settings)}
								/>
								<span>{formatMessage(financeMessages.transfer)}</span>
							</span>
						</InternalLink>
					</li>

					<li className={cn(styles.nav__itemFirst)}>
						<InternalLink to={routes.financeHistory.root} className={styles.nav__userButton}>
							<span>
								<img
									src={IconClockRewind}
									width={20}
									height={20}
									alt={formatMessage(messages.settings)}
								/>
								<span>{formatMessage(messages.history)}</span>
							</span>
						</InternalLink>
					</li>

					<li className={cn(styles.nav__itemFirst, styles.nav__itemFirst___separated)}>
						<InternalLink to={routes.settings.root} className={styles.nav__userButton}>
							<span>
								<img
									src={IconSettings}
									width={20}
									height={20}
									alt={formatMessage(messages.settings)}
								/>
								<span>{formatMessage(messages.settings)}</span>
							</span>
						</InternalLink>
					</li>

					<li className={styles.nav__itemFirst}>
						<button onClick={handleLogout} className={styles.nav__userButton} type="button">
							<span>
								<img src={IconLogout} width={20} height={20} alt={formatMessage(messages.logout)} />
								<span>{formatMessage(messages.logout)}</span>
							</span>
						</button>
					</li>
				</ul>
			</div>
			<NotificationsModal
				isOpen={isNotificationsModalOpened}
				onBack={() => toggleNotificationsModal(false)}
				onClose={() => {
					toggleNotificationsModal(false)
					onClose?.()
				}}
			/>
		</>
	)
}

export default observer(User)
